import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { 
  DollarSign, 
  RotateCcw, 
  Handshake, 
  TrendingDown, 
  Globe, 
  Building2,
  Lightbulb,
  AlertTriangle,
  Target,
  Users,
  Rocket,
  ShoppingCart,
  Building,
  Briefcase
} from "lucide-react";

interface TimelineEvent {
  id: string;
  date: string;
  quarter: string;
  title: string;
  description: string;
  type: 'funding' | 'pivot' | 'partnership' | 'market-down' | 'government' | 'product' | 'leadership' | 'shutdown';
  track: 'supplified' | 'market';
  icon: React.ComponentType<any>;
}

const timelineEvents: TimelineEvent[] = [
  // 2021
  {
    id: 'supplified-founded',
    date: '2021 Q3',
    quarter: '2021-Q3',
    title: 'Supplyfied Founded',
    description: 'Company established with B2B marketplace vision targeting hotels and restaurants',
    type: 'product',
    track: 'supplified',
    icon: Rocket
  },
  {
    id: 'vc-marketplace-boom',
    date: '2021 Q2-Q4',
    quarter: '2021-Q3',
    title: 'B2B Marketplace Funding Boom',
    description: 'VCs heavily funding B2B marketplaces post-COVID, blitzscaling trend at peak',
    type: 'funding',
    track: 'market',
    icon: DollarSign
  },
  
  // 2022
  {
    id: 'first-external-investment',
    date: '2022 Q1',
    quarter: '2022-Q1',
    title: 'First External Investment',
    description: 'Initial funding round completed to fuel early marketplace growth',
    type: 'funding',
    track: 'supplified',
    icon: DollarSign
  },
  {
    id: 'retailo-funding',
    date: '2022 Q1',
    quarter: '2022-Q1',
    title: 'Retailo Raises $36M Series A',
    description: 'Major regional competitor secures significant funding in B2B marketplace space',
    type: 'funding',
    track: 'market',
    icon: Building2
  },
  {
    id: 'pivot-planning',
    date: '2022 Q3',
    quarter: '2022-Q3',
    title: 'Pivot Planning Begins',
    description: 'Strategic reassessment of marketplace model due to unit economics challenges',
    type: 'pivot',
    track: 'supplified',
    icon: RotateCcw
  },
  {
    id: 'vc-pullback-signs',
    date: '2022 Q4',
    quarter: '2022-Q4',
    title: 'Early VC Pullback Signals',
    description: 'Global venture capital market shows first signs of tightening, higher scrutiny on unit economics',
    type: 'market-down',
    track: 'market',
    icon: TrendingDown
  },

  // 2023
  {
    id: 'hotel-classification',
    date: '2023 Q1',
    quarter: '2023-Q1',
    title: 'Hotel Classification Engine',
    description: 'Core technology platform launched, focus shifted from restaurants to hotels',
    type: 'product',
    track: 'supplified',
    icon: Building
  },
  {
    id: 'sary-struggles',
    date: '2023 Q1',
    quarter: '2023-Q1',
    title: 'Sary and Retailo Raises SAR 600M, Struggles Begin',
    description: 'Regional marketplace leader faces operational challenges despite massive funding',
    type: 'market-down',
    track: 'market',
    icon: AlertTriangle
  },
  {
    id: 'internal-funding',
    date: '2023 Q2',
    quarter: '2023-Q2',
    title: 'Internal Funding Bridge',
    description: 'Bridge funding secures runway during strategic transition, founder personal investment',
    type: 'funding',
    track: 'supplified',
    icon: DollarSign
  },
  {
    id: 'retailo-contraction',
    date: '2023 Q3',
    quarter: '2023-Q3',
    title: 'Retailo Market Contraction',
    description: 'Major B2B marketplace player starts facing significant market pressures',
    type: 'market-down',
    track: 'market',
    icon: TrendingDown
  },
  {
    id: 'pwc-partnership',
    date: '2023 Summer',
    quarter: '2023-Q3',
    title: 'PwC Strategic Partnership',
    description: 'Engagement with TDF and Ministry of Tourism, PwC writes minister-ready proposal',
    type: 'partnership',
    track: 'supplified',
    icon: Handshake
  },
  {
    id: 'misk-program',
    date: '2023 Fall',
    quarter: '2023-Q4',
    title: 'Misk/Plug and Play Program',
    description: 'Advisors recommend full pivot to SaaS, market model deemed unsustainable',
    type: 'pivot',
    track: 'supplified',
    icon: Lightbulb
  },
  {
    id: 'model-breaks',
    date: '2023 Q4',
    quarter: '2023-Q4',
    title: 'Marketplace Model Decision',
    description: 'Critical decision point reached - shut down sales, focus entirely on SaaS build',
    type: 'shutdown',
    track: 'supplified',
    icon: RotateCcw
  },

  // 2024
  {
    id: 'sac-launch',
    date: '2024 Q1',
    quarter: '2024-Q1',
    title: 'SAC Tool Launch',
    description: 'Strategic Advisory Consultant platform deployed, public sector engagement begins',
    type: 'product',
    track: 'supplified',
    icon: Target
  },
  {
    id: 'vc-traction-first',
    date: '2024 Q1',
    quarter: '2024-Q1',
    title: 'VC Mindset Shifts to Traction-First',
    description: 'Investment criteria fundamentally changes to prioritize proven revenue over growth potential',
    type: 'market-down',
    track: 'market',
    icon: TrendingDown
  },
  {
    id: 'cofounder-exit',
    date: '2024 Q2',
    quarter: '2024-Q2',
    title: 'Co-founder Exit',
    description: 'One co-founder exits, sole founder continues funding development via personal loans',
    type: 'leadership',
    track: 'supplified',
    icon: Users
  },
  {
    id: 'microsoft-partnership',
    date: '2024 Q3',
    quarter: '2024-Q3',
    title: 'Microsoft Co-sell Partnership',
    description: 'Strategic partnership established, revenue model switches to SaaS subscription',
    type: 'partnership',
    track: 'supplified',
    icon: Handshake
  },
  {
    id: 'sary-rescue',
    date: '2024 Q3',
    quarter: '2024-Q3',
    title: 'Sary Rescued via Merger',
    description: 'Emergency $110M injection through Sanabil + ShopUp merger demonstrates market consolidation',
    type: 'market-down',
    track: 'market',
    icon: AlertTriangle
  },

  // 2025
  {
    id: 'strategic-traction',
    date: '2025 Q1',
    quarter: '2025-Q1',
    title: 'Strategic Channel Traction',
    description: 'Growth momentum builds through partnerships, but runway remains critically low',
    type: 'product',
    track: 'supplified',
    icon: TrendingDown
  },
  {
    id: 'b2b-funding-collapse',
    date: '2025 Q1',
    quarter: '2025-Q1',
    title: 'Regional B2B Funding Collapse',
    description: 'Market correction accelerates with multiple company shutdowns, several players exit',
    type: 'market-down',
    track: 'market',
    icon: TrendingDown
  }
];

const ScrollTimeline = () => {
  const [visibleEvents, setVisibleEvents] = useState<Set<string>>(new Set());
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const eventId = entry.target.getAttribute('data-event-id');
          if (eventId && entry.isIntersecting) {
            setVisibleEvents(prev => new Set([...prev, eventId]));
          }
        });
      },
      { 
        threshold: 0.3,
        rootMargin: '0px 0px -20% 0px'
      }
    );

    const eventElements = document.querySelectorAll('[data-event-id]');
    eventElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const getEventIcon = (event: TimelineEvent) => {
    const IconComponent = event.icon;
    const baseClasses = "w-6 h-6";
    
    switch (event.type) {
      case 'funding':
        return <IconComponent className={`${baseClasses} text-green-600`} />;
      case 'pivot':
      case 'shutdown':
        return <IconComponent className={`${baseClasses} text-blue-600`} />;
      case 'partnership':
        return <IconComponent className={`${baseClasses} text-purple-600`} />;
      case 'market-down':
        return <IconComponent className={`${baseClasses} text-red-600`} />;
      case 'government':
        return <IconComponent className={`${baseClasses} text-emerald-600`} />;
      case 'product':
        return <IconComponent className={`${baseClasses} text-orange-600`} />;
      case 'leadership':
        return <IconComponent className={`${baseClasses} text-indigo-600`} />;
      default:
        return <IconComponent className={`${baseClasses} text-gray-600`} />;
    }
  };

  const getEventTypeEmoji = (type: string) => {
    switch (type) {
      case 'funding': return '💰';
      case 'pivot':
      case 'shutdown': return '🔄';
      case 'partnership': return '🤝';
      case 'market-down': return '📉';
      case 'government': return '🌍';
      case 'product': return '🚀';
      case 'leadership': return '👥';
      default: return '📍';
    }
  };

  // Group events by quarter for synchronized display
  const eventsByQuarter = timelineEvents.reduce((acc, event) => {
    if (!acc[event.quarter]) {
      acc[event.quarter] = { supplified: [], market: [] };
    }
    acc[event.quarter][event.track].push(event);
    return acc;
  }, {} as Record<string, { supplified: TimelineEvent[], market: TimelineEvent[] }>);

  const quarters = Object.keys(eventsByQuarter).sort();

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-12" ref={timelineRef}>
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold mb-4">The Parallel Journey</h2>
        <p className="text-muted-foreground text-lg">
          Supplyfied's evolution alongside market forces and industry shifts
        </p>
      </div>

      <div className="relative">
        {/* Central timeline line */}
        <div className="absolute left-1/2 transform -translate-x-0.5 w-1 bg-gradient-to-b from-primary/30 via-primary/60 to-primary/30 h-full"></div>

        {quarters.map((quarter, index) => {
          const quarterEvents = eventsByQuarter[quarter];
          const supplifiedEvents = quarterEvents.supplified;
          const marketEvents = quarterEvents.market;

          return (
            <div 
              key={quarter}
              data-event-id={`quarter-${quarter}`}
              className={`relative mb-16 transition-all duration-700 ${
                visibleEvents.has(`quarter-${quarter}`) 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-10'
              }`}
            >
              {/* Quarter marker */}
              <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-2 z-10">
                <div className="bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                  {quarter.replace('-', ' ')}
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-8 pt-8">
                {/* Supplified Track (Left) */}
                <div className="space-y-6">
                  <div className="text-right lg:pr-8">
                    <h3 className="text-lg font-semibold text-primary mb-4 flex items-center justify-end gap-2">
                      <span>Supplyfied Milestones</span>
                      <Building className="w-5 h-5" />
                    </h3>
                    
                    {supplifiedEvents.map((event) => (
                      <Card 
                        key={event.id}
                        data-event-id={event.id}
                        className={`mb-4 transition-all duration-500 delay-200 hover:shadow-lg ${
                          visibleEvents.has(event.id) 
                            ? 'opacity-100 translate-x-0' 
                            : 'opacity-0 translate-x-8'
                        }`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 p-2 rounded-lg bg-background border">
                              {getEventIcon(event)}
                            </div>
                            <div className="flex-1 text-right">
                              <div className="flex items-center justify-end gap-2 mb-2">
                                <span className="text-2xl">{getEventTypeEmoji(event.type)}</span>
                                <span className="text-sm font-medium text-primary bg-primary/10 px-2 py-1 rounded">
                                  {event.date}
                                </span>
                              </div>
                              <h4 className="font-semibold text-foreground mb-1">{event.title}</h4>
                              <p className="text-sm text-muted-foreground">{event.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Market Track (Right) */}
                <div className="space-y-6">
                  <div className="lg:pl-8">
                    <h3 className="text-lg font-semibold text-destructive mb-4 flex items-center gap-2">
                      <Globe className="w-5 h-5" />
                      <span>Market Forces</span>
                    </h3>
                    
                    {marketEvents.map((event) => (
                      <Card 
                        key={event.id}
                        data-event-id={event.id}
                        className={`mb-4 transition-all duration-500 delay-300 hover:shadow-lg ${
                          visibleEvents.has(event.id) 
                            ? 'opacity-100 translate-x-0' 
                            : 'opacity-0 -translate-x-8'
                        }`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 p-2 rounded-lg bg-background border">
                              {getEventIcon(event)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-medium text-destructive bg-destructive/10 px-2 py-1 rounded">
                                  {event.date}
                                </span>
                                <span className="text-2xl">{getEventTypeEmoji(event.type)}</span>
                              </div>
                              <h4 className="font-semibold text-foreground mb-1">{event.title}</h4>
                              <p className="text-sm text-muted-foreground">{event.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-16 p-6 bg-muted/30 rounded-lg">
        <h3 className="text-lg font-semibold mb-4 text-center">Timeline Legend</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-xl">💰</span>
            <span>Funding Events</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xl">🔄</span>
            <span>Pivots & Changes</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xl">🤝</span>
            <span>Partnerships</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xl">📉</span>
            <span>Market Downturns</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xl">🌍</span>
            <span>Government Relations</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xl">🚀</span>
            <span>Product Launches</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xl">👥</span>
            <span>Leadership Changes</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xl">📍</span>
            <span>Key Milestones</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScrollTimeline;